// Unified, minimalist header used across every public/client route
// via AppShell. Replaces both the old Navbar (which carried "Saloane
// / Hartă" text links that duplicated the marketplace itself) and
// the now-removed ExploreHeader.
//
// Layout:
//   left   — logo
//   right  — "Adaugă-ți salonul" discrete text link (desktop only,
//            and only when the visitor isn't already a salon owner
//            or admin) + a single user-menu avatar button
//
// The single avatar pattern covers both guests and authenticated
// users. Inside the dropdown:
//   guest      → Autentificare, Înregistrare, Adaugă-ți salonul
//   client     → Programările mele, Saloane favorite, Profilul meu,
//                Adaugă-ți salonul, Deconectare
//   salon_owner→ Dashboard salon, Profilul meu, Deconectare
//   specialist → Dashboard salon + the client menu (keeps client access)
//   admin      → Admin Panel, Profilul meu, Deconectare
//
// On mobile this is the ONLY interactive element besides the logo
// (per the brief — "Pe Mobil: Ascunde butoanele mari cu text").

import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FiUser,
  FiCalendar,
  FiHeart,
  FiLogOut,
  FiGrid,
  FiShield,
  FiMenu,
  FiLogIn,
  FiUserPlus,
  FiBriefcase,
  FiArrowLeft,
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import Wordmark from '../ui/Wordmark';
import { isNativeApp } from '../../utils/platform';

export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const isSalonOwner = user?.role === 'SALON_OWNER';
  // A specialist is dual-role: salon staff (gets the dashboard link) AND keeps
  // full client access (gets the client menu — they can still book).
  const isSpecialist = user?.role === 'SPECIALIST';
  const isClient = user?.role === 'CLIENT';
  // In the native B2C app the "add your salon" CTA and the salon/admin
  // dashboard links are removed — those surfaces aren't in the app at all.
  const showB2BCTA = !isSalonOwner && !isAdmin && !isNativeApp;
  // The bottom tab bar is the mobile nav for everyone (renders for guests too).
  // Native: hide the top-right avatar/menu entirely. Web: keep it mounted, but
  // the button is hidden for GUESTS on mobile (`hidden md:flex` below) so it
  // doesn't duplicate the bottom bar — desktop and logged-in web users keep it
  // (salon owners reach the dashboard through it on mobile web).
  const showAccountMenu = !isNativeApp;
  // The public salon detail page (/salon/:slug, not the B2B /salon/dashboard) is
  // an immersive Fresha-style view on mobile: a full-bleed gallery to the screen
  // top + its own sticky header/tabs. Hide this global bar there on mobile only;
  // desktop keeps it (the page uses a sidebar layout, not the immersive shell).
  const isSalonDetail = /^\/salon\/(?!dashboard)/.test(location.pathname);
  // Sub-pages get a back arrow in place of the logo so FAQ / Contact / legal /
  // booking / etc. are always escapable. Shown on native AND web mobile (the
  // render below makes the arrow md:hidden and keeps the logo on desktop).
  // Bottom-nav roots keep the logo; /profile has its own back header;
  // /salon/:slug hides this whole bar already.
  const ROOT_PATHS = new Set(['/', '/my-bookings']);
  const showBack = !ROOT_PATHS.has(location.pathname)
    && !location.pathname.startsWith('/profile')
    && !isSalonDetail;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Sub-page back arrow. On a deep-link / fresh load (shared link, search engine,
  // QR) there's no in-app history to pop — react-router keeps location.key as
  // 'default' ONLY for that initial entry — and navigate(-1) there would either
  // do nothing or eject the user out of the app entirely (back to Google). Fall
  // back to home so Back always lands somewhere inside the app.
  const handleBack = () => {
    if (location.key === 'default') navigate('/');
    else navigate(-1);
  };

  const initial = user?.firstName?.[0] || user?.email?.[0] || 'U';

  return (
    // pt-[env(safe-area-inset-top)] keeps the logo/avatar row clear of the iOS
    // status bar (notch / Dynamic Island) in the Capacitor shell, where
    // viewport-fit=cover lets content run under it. Zero on web/Android, so no
    // visual change there; the cream background fills behind the status bar.
    <header
      className={`${isSalonDetail ? 'hidden sm:block' : ''} sticky top-0 z-50 transition-shadow pt-[env(safe-area-inset-top,0)] ${
        scrolled ? 'border-b border-ink/10 bg-cream-100/90 backdrop-blur shadow-sm' : 'bg-cream-100'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        {/* Sub-pages: back arrow on mobile (app + web), logo on desktop.
            Root pages: always the logo. */}
        {showBack ? (
          <>
            <button
              type="button"
              onClick={handleBack}
              aria-label="Înapoi"
              // Show the back arrow on mobile AND on native at every width. The
              // native app (incl. "iOS app on Mac", a wide window) has no
              // top-right menu, so on sub-pages like /terms the md:hidden arrow
              // left the Mac with only the logo (→ home, not Back) and no way
              // back. Web desktop keeps the logo (its top menu handles nav).
              className={`-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-ink-700 active:bg-ink/5 ${isNativeApp ? '' : 'md:hidden'}`}
            >
              <FiArrowLeft className="h-6 w-6" />
            </button>
            {/* Web desktop keeps the logo (its top menu handles nav); the
                native app never shows it here, so don't mount it at all. */}
            {!isNativeApp && (
              <Link to="/" className="hidden md:flex flex-shrink-0 items-center">
                <Wordmark className="text-2xl sm:text-3xl" />
              </Link>
            )}
          </>
        ) : (
          <Link to="/" className="flex flex-shrink-0 items-center">
            <Wordmark className="text-2xl sm:text-3xl" />
          </Link>
        )}

        {/* Right cluster. The text B2B CTA is desktop-only (per spec),
            but the avatar/menu button stays mounted on every viewport —
            it's the only path to Dashboard / Deconectare for salon
            owners and admins on mobile, since BottomNav's tabs are
            client-shaped (Favorite / Programări / Profil) and don't
            cover their needs. */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* The B2B "Adaugă-ți salonul" CTA lives entirely inside the dropdown
              menu now (on every viewport) — it kept cluttering the top bar next
              to the logo. See the menu entries below. */}

          {/* Single avatar/menu trigger. Same affordance for guest and
              authenticated users — the dropdown's contents are what
              changes. */}
          {showAccountMenu && (
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={user ? 'Meniu utilizator' : 'Meniu'}
            aria-expanded={menuOpen}
            className={`${!user ? 'hidden md:flex' : 'flex'} items-center gap-2 rounded-full border border-ink/15 bg-cream-50 py-1.5 pl-3.5 pr-1.5 transition-shadow hover:shadow`}
          >
            <FiMenu className="h-4 w-4 sm:h-5 sm:w-5 text-ink-600" aria-hidden="true" />
            {/* "Meniu" label (Fresha-style) — desktop only, keeps the trigger
                compact on phones. */}
            <span className="hidden text-sm font-medium text-ink-700 lg:inline">Meniu</span>
            {user ? (
              <span className={`flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full text-xs sm:text-sm font-semibold text-white ${
                isAdmin ? 'bg-gradient-to-br from-red-500 to-red-700' :
                isSalonOwner ? 'bg-gradient-to-br from-purple-500 to-purple-700' :
                'bg-gradient-to-br from-orange-400 to-orange-600'
              }`}>
                {initial}
              </span>
            ) : (
              <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-ink-500 text-white">
                <FiUser className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
              </span>
            )}
          </button>
          )}

          {showAccountMenu && menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-4 top-14 z-20 w-60 overflow-hidden rounded-2xl border border-ink/10 bg-cream-50 py-2 shadow-xl sm:right-6 lg:right-8">
                {user ? (
                  <>
                    <div className="border-b border-ink/10 px-4 py-3">
                      <div className="text-sm font-medium text-ink">{user.firstName} {user.lastName}</div>
                      <div className="truncate text-xs text-ink-500">{user.email}</div>
                    </div>
                    {isAdmin && !isNativeApp && (
                      <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                        <FiShield className="h-4 w-4" /> Admin Panel
                      </Link>
                    )}
                    {(isSalonOwner || isSpecialist) && !isNativeApp && (
                      <Link to="/salon/dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                        <FiGrid className="h-4 w-4" /> Dashboard Salon
                      </Link>
                    )}
                    {(isClient || isSpecialist) && (
                      <>
                        <Link to="/my-bookings" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                          <FiCalendar className="h-4 w-4" /> Programările mele
                        </Link>
                        <Link to="/profile?tab=favorites" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                          <FiHeart className="h-4 w-4" /> Saloane favorite
                        </Link>
                      </>
                    )}
                    <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                      <FiUser className="h-4 w-4" /> Profilul meu
                    </Link>
                    {showB2BCTA && (
                      <Link to="/pro" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                        <FiBriefcase className="h-4 w-4" /> Adaugă-ți salonul
                      </Link>
                    )}
                    <hr className="my-1 border-ink/10" />
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
                    >
                      <FiLogOut className="h-4 w-4" /> Deconectare
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-primary-50 hover:text-primary-700">
                      <FiLogIn className="h-4 w-4" /> Autentificare
                    </Link>
                    <Link to="/register" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                      <FiUserPlus className="h-4 w-4" /> Înregistrare
                    </Link>
                    {!isNativeApp && (
                      <>
                        <hr className="my-1 border-ink/10" />
                        <Link to="/pro" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                          <FiBriefcase className="h-4 w-4" /> Adaugă-ți salonul
                        </Link>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
